import { Alert, AlertTitle, Box, LinearProgress, Typography } from "@mui/material"
import { AddOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { OnChangeFn, RowSelectionState, SortingState } from "@tanstack/react-table"
import React, { useCallback, useEffect, useRef } from "react"
import type { UseFormReturn } from "react-hook-form"
import { FormProvider } from "react-hook-form"
import type { Client } from "urql"

import { useProductTable } from "./hooks/useProductTable"
import type { ProductSearchFilters } from "./stores/useProductsSearchStore"
import { useProductSearchStore } from "./stores/useProductsSearchStore"
import type { ProductSortableAccessorKey } from "./utils/sorftFieldMap"
import { accessorKeyToProductSortField, productSortFieldToAccessoryKey } from "./utils/sorftFieldMap"

import { Button } from "@/components/common/Button"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { Company, Product } from "@/graphql/codegen/graphql"
import { CompanyStatus, SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { useCompanyEditSearch } from "@/screens/Companies/hooks"
import { useProductActionsStore } from "@/screens/Companies/products/stores/useProductsActions"
import AddProductDialog from "@/screens/Products/components/dialogs/AddProductDialog"
import ProductEditingDialogs from "@/screens/Products/components/dialogs/ProductEditingDialogs"
import type { ProductAddFormValues } from "@/screens/Products/schemas/productFormSchema"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"
import { isMatchingLocationAssociation } from "@/utils/isMatchingLocationAssociation"
import useDebounce from "@/utils/useDebounce"
import { isTableRow } from "@/utils/utils"

interface ProductListTableProps {
  company: Company
  methods: UseFormReturn<ProductAddFormValues>
  open: boolean
  data: Product[]
  fetchingProducts: boolean
  error: Error | undefined
  executeSearch: (client: Client, filters: ProductSearchFilters, skipFilterCheck?: boolean) => Promise<void>
  loadMore: (client: Client, filters: ProductSearchFilters) => Promise<void>
  setOpen: (open: boolean) => void
  selected: Record<string, boolean>
  setSelected: (selected: Record<string, boolean>) => void
  onSelectionUpdate?: (products: Product[]) => void
}

export const ProductListTable: React.FC<ProductListTableProps> = ({
  company: { companyId, status: companyStatus },
  methods,
  open,
  data,
  fetchingProducts,
  error,
  executeSearch,
  loadMore,
  setOpen,
  selected,
  setSelected,
  onSelectionUpdate,
}) => {
  const navigate = useNavigate()
  const currentSearch = useCompanyEditSearch()

  const { category, query, field, order, ...restSearchParams } = currentSearch

  const setCompanyId = useProductSearchStore(({ setCompanyId }) => setCompanyId)
  const selectedItemData = useRef<Product[]>([])

  useEffect(() => {
    setCompanyId(companyId)
  }, [companyId, setCompanyId])

  const { columns, sorting, setSorting } = useProductTable({
    sortBy: field,
    sortOrder: order,
  })

  useEffect(() => {
    if (!field) return
    const tableSortField = productSortFieldToAccessoryKey.get(field)
    if (!tableSortField) return

    setSorting([
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ])
  }, [field, order, setSorting])

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const debouncedExecuteSearch = useDebounce((filters: ProductSearchFilters) => {
    executeSearch?.(queryClient, filters)
  }, DEBOUNCE_DELAY_MS)

  const handleSortingChange = useCallback(
    (updaterFn: (state: SortingState) => SortingState) => {
      const newSorting = updaterFn(sorting)
      if (newSorting.length > 0) {
        const sort = newSorting[0]
        const key = sort.id as ProductSortableAccessorKey
        const field = accessorKeyToProductSortField.get(key)
        const params = {
          ...restSearchParams,
          query,
          category,
          field,
          order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
        }
        const refinedParams = getRefinedSearchParams(params)
        void navigate({
          search: refinedParams.search,
          to: "/app/companies/$companyId/edit/products",
          params: { companyId },
          replace: true,
        })
      }
    },
    [sorting, restSearchParams, query, category, navigate, companyId]
  )

  const handleRowSelectionChange = (
    updaterOrValue: ((old: RowSelectionState) => RowSelectionState) | RowSelectionState
  ) => {
    const newSelection = typeof updaterOrValue === "function" ? updaterOrValue(selected) : updaterOrValue

    const mergedSelection = { ...selected }

    // Apply additions and updates from newSelection
    Object.keys(newSelection).forEach((id) => {
      if (newSelection[id]) {
        mergedSelection[id] = true
      }
    })

    // Handle removals: if ID is in current data (visible) but not selected in newSelection, remove it
    const currentDataIds = new Set(data.map((p) => p.productId.toString()))
    Object.keys(selected).forEach((id) => {
      if (currentDataIds.has(id) && !newSelection[id]) {
        delete mergedSelection[id]
      }
    })

    setSelected(mergedSelection)
    const selectedIds = Object.keys(mergedSelection).filter((id) => mergedSelection[id])

    // Preserve previously cached products that are still selected
    const preservedProducts = selectedItemData.current.filter((p) => selectedIds.includes(p.productId.toString()))

    const newProducts = data.filter((p) => {
      const id = p.productId.toString()
      return selectedIds.includes(id) && !preservedProducts.some((existing) => existing.productId === p.productId)
    })

    selectedItemData.current = [...preservedProducts, ...newProducts]

    // Notify parent of cached product changes
    onSelectionUpdate?.(selectedItemData.current)
  }

  const handleRowClick = useCallback(
    (value: unknown) => {
      if (isTableRow<Product>(value)) {
        navigate({ to: `/app/product/${value.original.productId}/edit` })
      }
    },
    [navigate]
  )

  const [throttledLoadMore] = useThrottle(() => loadMore(queryClient, { ...currentSearch, companyId }), 200)
  const onScroll = useScrollLoadMore({
    fetching: fetchingProducts,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const clearStore = useProductSearchStore(({ clearStore }) => clearStore)

  const productAtAction = useProductActionsStore((state) => state.productAtAction)
  const setOpenedDialog = useProductActionsStore((state) => state.setOpenedDialog)

  const refreshProducts = async () => {
    await executeSearch?.(queryClient, currentSearch, true)
    setOpenedDialog(null)
  }

  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [companyId, clearStore])

  useEffect(() => {
    debouncedExecuteSearch(currentSearch)
  }, [debouncedExecuteSearch, currentSearch])

  if (!fetchingProducts && data.length === 0 && Object.keys(currentSearch).every((key) => !currentSearch[key])) {
    return (
      <Box my={2}>
        <Alert
          icon={<WarningOutlined color='primary' />}
          severity='info'
          variant='filled'
          classes={{
            message: "w-full flex flex-row justify-between",
          }}
        >
          <Box>
            <AlertTitle className='font-normal leading-7'>Add company products and pricing by location</AlertTitle>
            <Box className='w-full font-thin'>Products and pricing need to be added for each ship from location</Box>
          </Box>
          <Box className='flex items-center'>
            <Button size='small' appearance='outlined' onClick={() => setOpen(true)}>
              <AddOutlined />
              Add Product
            </Button>
          </Box>
        </Alert>
        <FormProvider {...methods}>
          <AddProductDialog
            open={open}
            onClose={() => {
              setOpen(false)
            }}
            onSubmit={methods.handleSubmit}
          />
        </FormProvider>
      </Box>
    )
  }

  return (
    <Box className='flex h-full flex-col'>
      {fetchingProducts && <LinearProgress className='sticky top-0 z-10' />}
      <AerosBaseTable
        features={{
          table: {
            stickyHeader: true,
          },
          tanstackOptions: {
            data,
            columns,
            state: {
              rowSelection: selected,
              sorting,
              enableExpanded: false,
            },
            manualSorting: true,
            enableSorting: true,
            enableMultiSort: false,
            enableSortingRemoval: false,

            enableMultiRowSelection: true,
            enableRowSelection:
              companyStatus === CompanyStatus.Active &&
              ((row) =>
                isMatchingLocationAssociation({
                  product: row.original,
                  rowSelectionState: selected,
                  products: selectedItemData.current,
                })),
            getRowId: (row) => row.productId.toString(),
            onRowSelectionChange: handleRowSelectionChange,
            onSortingChange: handleSortingChange as OnChangeFn<SortingState>,
          },
        }}
        slotProps={{
          container: {
            ref: tableContainerRef,
            onScroll,
          },
          bodyRow: {
            className: "hover:bg-primary-100 cursor-pointer",
            onClick: handleRowClick,
          },
        }}
        noDataMessage='No products added'
      />
      {error && (
        <Typography color='error' className='p-4'>
          {error.message}
        </Typography>
      )}
      {productAtAction?.productId && <ProductEditingDialogs product={productAtAction} onSubmit={refreshProducts} />}
    </Box>
  )
}
